import {TranslationDict} from '@shared/types';
import {Category, SubCategory} from '../category';

export enum EventStatus {
  active = 'active',
  closed = 'closed',
  waitingForResult = 'waiting_for_result',
  disputed = 'disputed',
  null = 'null',
}

const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  [EventStatus.null]: 'Not published',
  [EventStatus.active]: 'Active',
  [EventStatus.closed]: 'Closed',
  [EventStatus.waitingForResult]: 'Waiting for result',
  [EventStatus.disputed]: 'Disputed',
};

export const formatEventStatus = (status: EventStatus): string =>
  EVENT_STATUS_LABELS[status] ?? status;

export enum EventDisplayType {
  fixedLive = 'fixed_live',
  fixedStart = 'fixed_start',
  fixedEnd = 'fixed_end',
  fixedStartEnd = 'fixed_start_end',
}

const EVENT_DISPLAY_TYPE_LABELS: Record<EventDisplayType, string> = {
  [EventDisplayType.fixedLive]: 'Fixed live',
  [EventDisplayType.fixedStart]: 'Fixed start',
  [EventDisplayType.fixedEnd]: 'Fixed end',
  [EventDisplayType.fixedStartEnd]: 'Fixed start & end',
};

export const formatEventDisplayType = (type: EventDisplayType): string =>
  EVENT_DISPLAY_TYPE_LABELS[type] ?? type;

export enum ParticipantLogoOption {
  default = 'default',
  defaultStroke = 'default_stroke',
  player = 'player',
  custom = 'custom',
  topic = 'topic',
  unique = 'unique',
}

const PARTICIPANT_LOGO_OPTION_LABELS: Record<ParticipantLogoOption, string> = {
  [ParticipantLogoOption.default]: 'Shield',
  [ParticipantLogoOption.defaultStroke]: 'Shield + stroke',
  [ParticipantLogoOption.player]: 'Player',
  [ParticipantLogoOption.custom]: 'Custom',
  [ParticipantLogoOption.topic]: 'Topic',
  [ParticipantLogoOption.unique]: 'Unique',
};

export const formatParticipantLogoOption = (
  option: ParticipantLogoOption,
): string => PARTICIPANT_LOGO_OPTION_LABELS[option] ?? option;

export enum EventResult {
  yes = 'yes',
  no = 'no',
  waitingForResult = 'waiting_for_result',
  disputed = 'disputed',
  null = 'null',
}

export enum EventAuditActor {
  admin = 'admin',
  eventProvider = 'event_provider',
  poller = 'poller',
}

const EVENT_AUDIT_ACTOR_LABELS: Record<EventAuditActor, string> = {
  [EventAuditActor.admin]: 'Admin',
  [EventAuditActor.eventProvider]: 'Event provider',
  [EventAuditActor.poller]: 'Poller',
};

export const formatEventAuditActor = (actor: EventAuditActor): string =>
  EVENT_AUDIT_ACTOR_LABELS[actor] ?? actor;

export interface GroupEvent {
  description: string;
  eventId: string;
  groupOrder: number;
  groupType: number;
  id: string;
  logoUrl: string;
  name: string;
  shortLogoUrl: string;
  shortName: string;
  tradingVolume: number;
  yesPrice: number;
  noPrice: number;
}
export interface TopicEvent {
  id: string;
  topicId: string;
  name: string;
  logoUrl: string | null;
}

export interface EventParticipant {
  id: string;
  name: string;
  logoUrl: string | null;
  logoUrlStroke: string | null;
  logoUrlPlayer: string | null;
  logoUrlCustom: string | null;
  nation: string | null;
}

export interface EventLog {
  id: string;
  action: string;
  reason: string;
  timestamp: Date;
  actorType: EventAuditActor;
  actorId: string | null;
  statusBefore: EventStatus | null;
  statusAfter: EventStatus | null;
  resultBefore: EventResult | null;
  resultAfter: EventResult | null;
}

export interface Event {
  id: string;
  eventId: string;
  name: string;
  nameTranslations?: TranslationDict;
  shortName?: string;
  shortNameTranslations?: TranslationDict;
  holders: number;
  participants: number;
  description: string;
  descriptionTranslations?: TranslationDict;
  category: Category;
  subCategories: SubCategory[];
  geoSlugs: string[];
  startDate: Date;
  endDate: Date;
  liveDate: Date | null;
  isFavorite: boolean;
  isPromoted?: boolean;
  logoUrl: string;
  shortLogoUrl: string;
  yesPrice: number;
  noPrice: number;
  yesTextTranslations?: TranslationDict;
  noTextTranslations?: TranslationDict;
  isLive: boolean;
  autoPublishEnabled: boolean;
  yesText: string;
  noText: string;
  overview?: string;
  overviewTranslations?: TranslationDict;
  sourceOfTruth?: string;
  sourceOfTruthTranslations?: TranslationDict;
  rules?: string;
  rulesTranslations?: TranslationDict;
  tradingVolume: number;
  status: EventStatus;
  result: EventResult;
  group: GroupEvent[];
  groupId?: string | null;
  topic?: TopicEvent | null;
  displayType: EventDisplayType;
  participantLogoOption?: ParticipantLogoOption | null;
  participantShortLogoOption?: ParticipantLogoOption | null;
  participantId?: string | null;
  externalParticipantId?: string | null;
  yesParticipantId?: string | null;
  externalYesParticipantId?: string | null;
  noParticipantId?: string | null;
  externalNoParticipantId?: string | null;
  participant?: EventParticipant | null;
  yesParticipant?: EventParticipant | null;
  noParticipant?: EventParticipant | null;
}

const EVENT_RESULT_LABELS: Record<EventResult, string> = {
  [EventResult.yes]: 'Yes',
  [EventResult.no]: 'No',
  [EventResult.waitingForResult]: 'Waiting for result',
  [EventResult.disputed]: 'Disputed',
  [EventResult.null]: 'Not determined',
};

export const formatEventResult = (result: EventResult): string =>
  EVENT_RESULT_LABELS[result] ?? result;

export const STATUS_TRANSITIONS: Record<EventStatus, EventStatus[]> = {
  [EventStatus.null]: [EventStatus.null, EventStatus.active],
  [EventStatus.active]: [
    EventStatus.active,
    EventStatus.closed,
    EventStatus.waitingForResult,
    EventStatus.disputed,
  ],
  [EventStatus.waitingForResult]: [
    EventStatus.closed,
    EventStatus.waitingForResult,
    EventStatus.disputed,
  ],
  [EventStatus.disputed]: [
    EventStatus.closed,
    EventStatus.waitingForResult,
    EventStatus.disputed,
  ],
  [EventStatus.closed]: [EventStatus.closed],
};
